import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Menu,
  Icons,
  PageHeader,
  Text,
  Grid,
  DataTable,
  StatusChip,
  BadgeColor,
  DataTableSort,
} from "@pushpress/shared-components";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import AddPlanSideDrawer from "./AddPlanSideDrawer";
import {
  useAssignPlan,
  usePlansDiscountsQuery,
} from "../../api/migrationAssistantApi.hooks";
import { defaultToastOptions } from "../../../Appointments/AppointmentsUtils";
import {
  getMemberStatus,
  getPaymentBillingTypeLabel,
  getPlanInfo,
  MemberSortType,
  sortMembers,
} from "../../helpers";
import UserAvatar from "../../../../components/common/UserAvatar/UserAvatar";
import { Plan } from "../../../../apis/subscriptionApi";
import { joinStrings } from "../../../../utils/misc";
import { useAnalytics } from "../../../../components/providers/AnalyticsProvider";
import type { MemberDataType } from "../../types";
import { AlertCircle, CircleCheck } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@pushpress/pushpress-ui";

type AssignPlansTableType = {
  membersData: MemberDataType[];
  activePlansData: Plan[];
  setShowAssignPlansPage: (showAssignPlansPage: boolean) => void;
};

export default function AssignPlansTable({
  activePlansData,
  membersData,
  setShowAssignPlansPage,
}: AssignPlansTableType) {
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useAssignPlan();
  const { data: plansDiscountsData, isFetching: plansDiscountsFetching } =
    usePlansDiscountsQuery();

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<MemberDataType | null>(
    null,
  );

  const handlePlanRemove = useCallback(
    async (member: MemberDataType) => {
      mutate(
        {
          id: member.id,
          planUuid: "",
          planDiscountUuid: "",
          planStartBilling: null,
          planBillingType: "",
          needPlan: true,
          planRemainingSessions: null,
          planEndDate: null,
        },
        {
          onSuccess: () => {
            toast.success("Plan removed", defaultToastOptions);
            queryClient.invalidateQueries({
              queryKey: ["fetchMigratingMembers"],
            });
          },
          onError: (error) => {
            toast.error(
              "There was an error removing plan",
              defaultToastOptions,
            );
            trackEvent("Migration Assistant Error", {
              error: "ma_patch_api_remove_plan_error",
              api_error: JSON.stringify(error),
            });
          },
        },
      );
    },
    [mutate, queryClient, trackEvent],
  );

  const handleDoesNotHavePlan = useCallback(
    async (member: MemberDataType) => {
      mutate(
        {
          id: member.id,
          planUuid: "",
          planDiscountUuid: "",
          planStartBilling: null,
          planBillingType: "",
          needPlan: false,
          planRemainingSessions: null,
          planEndDate: null,
        },
        {
          onSuccess: () => {
            toast.success("Plan removed", defaultToastOptions);
            queryClient.invalidateQueries({
              queryKey: ["fetchMigratingMembers"],
            });
          },
          onError: (error) => {
            toast.error(
              "There was an error removing plan",
              defaultToastOptions,
            );
            trackEvent("Migration Assistant Error", {
              error: "ma_patch_api_add_has_no_plan_error",
              api_error: JSON.stringify(error),
            });
          },
        },
      );
    },
    [mutate, queryClient, trackEvent],
  );

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        isSortable: true,
        width: "40%",
        widthMobile: "auto",
        cell: (member: MemberDataType) => (
          <Grid
            container
            alignItems="center"
            className={member.parentEmail ? "tw-pl-6" : ""}
          >
            <Grid item>
              <UserAvatar data={member} size="medium" />
            </Grid>
            <Grid item>
              <Text variant="body-lg" color="wolf-800">
                {joinStrings(member.firstName, member.lastName)}
              </Text>
              <Text variant="body-lg" color="wolf-600">
                {member.email || "—"}
              </Text>
            </Grid>
          </Grid>
        ),
      },
      {
        key: "plan",
        header: "Plan",
        isSortable: true,
        width: "40%",
        widthMobile: "auto",
        cell: (member: MemberDataType) => (
          <>
            {!member?.needPlan && (
              <StatusChip color={BadgeColor.Gray} label="NO PLAN" />
            )}
            {member?.planUuid && (
              <Grid item>
                <Text variant="body-lg" color="wolf-800">
                  {getPlanInfo(activePlansData, member.planUuid)?.name ?? ""}
                </Text>
                <Text variant="body-lg" color="wolf-600">
                  {getPaymentBillingTypeLabel(member.planBillingType)}
                </Text>
              </Grid>
            )}
            {!member.planUuid && member.needPlan && (
              <Button
                buttonType="secondary"
                text="Plan"
                icon={Icons.Plus}
                iconLocation="left"
                onClick={() => {
                  setSelectedMember(member);
                  setIsDrawerOpen(true);
                }}
              />
            )}
          </>
        ),
      },
      {
        key: "status",
        hideOnMobile: true,
        cell: (member: MemberDataType) => {
          const status = getMemberStatus(member, membersData);
          if (!status) return null;
          return (
            <div className="tw-flex tw-items-center tw-gap-x-2">
              <Tooltip>
                <TooltipTrigger>
                  {status.level === "success" && (
                    <CircleCheck className="tw-text-green-500" />
                  )}
                  {status.level === "warning" && (
                    <AlertCircle className="tw-text-amber-500" />
                  )}
                </TooltipTrigger>
                <TooltipContent className="tw-max-w-80 tw-bg-slate-800">
                  {status.description}
                </TooltipContent>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [activePlansData, membersData],
  );

  const [sort, setSort] = useState<DataTableSort<MemberSortType>>({
    key: "name",
    order: "asc",
  });

  const handleSort = (value: DataTableSort<MemberSortType>) => {
    setSort(value);
  };

  const data = useMemo(() => {
    return sortMembers(membersData, sort, activePlansData);
  }, [membersData, sort, activePlansData]);

  const renderRowMenu = useCallback(
    (member: MemberDataType, closeMenu: VoidFunction) => {
      return (
        <Menu>
          {member?.planUuid && (
            <>
              <Menu.Item
                label="Edit plan"
                action={() => {
                  setSelectedMember(member);
                  setIsDrawerOpen(true);
                  closeMenu();
                }}
              />
              <Menu.Item
                label="Remove plan"
                action={async () => {
                  await handlePlanRemove(member);
                  closeMenu();
                }}
              />
              <Menu.Item
                label="Plan not needed"
                action={async () => {
                  await handleDoesNotHavePlan(member);
                  closeMenu();
                }}
              />
            </>
          )}
          {!member?.needPlan && (
            <>
              <Menu.Item
                label="Add plan"
                action={() => {
                  setSelectedMember(member);
                  setIsDrawerOpen(true);
                  closeMenu();
                }}
              />
            </>
          )}
          {member?.needPlan && !member?.planUuid && (
            <>
              <Menu.Item
                label="Add plan"
                action={() => {
                  setSelectedMember(member);
                  setIsDrawerOpen(true);
                  closeMenu();
                }}
              />
              <Menu.Item
                label="Plan not needed"
                action={async () => {
                  await handleDoesNotHavePlan(member);
                  closeMenu();
                }}
              />
            </>
          )}
        </Menu>
      );
    },
    [
      handleDoesNotHavePlan,
      handlePlanRemove,
      setSelectedMember,
      setIsDrawerOpen,
    ],
  );

  return (
    <Grid item>
      <Grid item marginY={3}>
        <Button
          text="Back to Migration assistant"
          buttonType="text"
          icon={Icons.ArrowLeft}
          iconLocation="left"
          onClick={() => {
            queryClient.invalidateQueries({
              queryKey: ["fetchMigratingMembers"],
            });
            setShowAssignPlansPage(false);
          }}
        />
      </Grid>
      <Grid item marginY={3}>
        <PageHeader title="Assign plans" />
      </Grid>
      <Grid item marginY={3}>
        <DataTable
          data={data}
          columns={columns}
          sort={sort}
          getRowId={(row) => String(row.id)}
          onSort={handleSort}
          renderRowMenu={renderRowMenu}
          loading={isLoading || plansDiscountsFetching}
        />
      </Grid>
      {isDrawerOpen && selectedMember && plansDiscountsData && (
        <AddPlanSideDrawer
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          setSelectedMember={setSelectedMember}
          member={selectedMember}
          plansData={activePlansData}
          plansDiscountsData={plansDiscountsData}
        />
      )}
    </Grid>
  );
}
